import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { UnopenedChest } from '@services';
import { useOpenChest } from '@hooks';
import { OpenChestModal } from './OpenChestModal';
import { ChestRewardModal } from './ChestRewardModal';
import shopChestNormal from '@/assets/shop/shop-chest-normal.webp';
import shopChestPlatinum from '@/assets/shop/shop-chest-platinum.webp';
import shopChestRare from '@/assets/shop/shop-chest-rare.webp';
import shopChestLegendary from '@/assets/shop/shop-chest-legendary.webp';
import CloseButton from '@/assets/svgs/CloseButton';

interface InventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  unopenedChests: UnopenedChest[];
  onChestOpened?: () => void;
}

const chestImageMap: Record<number, string> = {
  1: shopChestNormal,
  2: shopChestPlatinum,
  3: shopChestRare,
  4: shopChestLegendary,
};

const chestNameMap: Record<number, string> = {
  1: 'Normal',
  2: 'Platinum',
  3: 'Rare',
  4: 'Legendary',
};

export const InventoryModal = ({
  isOpen,
  onClose,
  unopenedChests,
  onChestOpened,
}: InventoryModalProps) => {
  const [selectedChest, setSelectedChest] = useState<UnopenedChest | null>(
    null
  );
  const [isOpenChestModalOpen, setIsOpenChestModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(0);

  const openChestMutation = useOpenChest();

  // Group chests by chestId and count them
  const chestCounts = unopenedChests.reduce(
    (acc, chest) => {
      acc[chest.chestId] = (acc[chest.chestId] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  );

  const handleChestClick = (chestId: number) => {
    // Find the first chest with this chestId
    const chest = unopenedChests.find((c) => c.chestId === chestId);
    if (chest) {
      setSelectedChest(chest);
      setIsOpenChestModalOpen(true);
    }
  };

  const handleOpenChest = async () => {
    if (!selectedChest || openChestMutation.isPending) return;

    try {
      const response = await openChestMutation.mutateAsync(selectedChest.id);

      // Close open chest modal
      setIsOpenChestModalOpen(false);
      setSelectedChest(null);

      // Show reward modal with rewards
      setRewardAmount(response.rewards);
      setIsRewardModalOpen(true);
    } catch (error) {
      console.error('Failed to open chest:', error);
    }
  };

  const handleClaimReward = () => {
    setIsRewardModalOpen(false);
    setRewardAmount(0);

    // Notify parent to refresh data after claiming
    onChestOpened?.();
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />

            {/* Bottom Sheet Container */}
            <motion.div
              className="fixed left-0 right-0 bottom-0 z-[101] max-w-[var(--app-max-width)] mx-auto"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              {/* Modal Content */}
              <div
                className="relative flex flex-col items-center bg-[#C2CCDF] rounded-t-2xl"
                style={{
                  padding: '12px 6px 6px',
                  gap: '16px',
                  width: '100%',
                  height: '573px',
                  backdropFilter: 'blur(4px)',
                }}
              >
                {/* Close Button - Top Right */}
                <motion.button
                  className="absolute flex items-center justify-center"
                  style={{
                    width: '24px',
                    height: '24px',
                    right: '12px',
                    top: '12px',
                  }}
                  onClick={onClose}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  <CloseButton />
                </motion.button>

                {/* Header Title */}
                <h1
                  className="font-lilita text-center text-white"
                  style={{
                    fontSize: '32px',
                    lineHeight: '37px',
                    textShadow: '1px 2px 0px #000000',
                  }}
                >
                  Inventory
                </h1>

                {/* Chest Grid Container */}
                <div
                  className="flex flex-col items-start bg-[#8391A6] rounded-xl flex-grow overflow-y-auto"
                  style={{
                    width: '100%',
                    maxWidth: '363px',
                    padding: '20px 12px',
                    gap: '16px',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  {Object.keys(chestCounts).length > 0 ? (
                    <div
                      className="flex flex-row flex-wrap items-start w-full"
                      style={{ gap: '20px' }}
                    >
                      {Object.entries(chestCounts).map(([chestId, count]) => {
                        const id = Number(chestId);
                        return (
                          <motion.div
                            key={chestId}
                            className="flex flex-col items-center relative cursor-pointer"
                            style={{
                              width: '99px',
                              height: '99px',
                              gap: '4px',
                            }}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            onClick={() => handleChestClick(id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            {/* Chest Image */}
                            <img
                              src={chestImageMap[id]}
                              alt={chestNameMap[id]}
                              style={{
                                width: '72px',
                                height: '72px',
                                objectFit: 'contain',
                              }}
                            />

                            {/* Chest Name */}
                            <p
                              className="font-lilita text-white text-center"
                              style={{
                                fontSize: '20px',
                                lineHeight: '23px',
                                textShadow: '1px 2px 0px #000000',
                              }}
                            >
                              {chestNameMap[id]}
                            </p>

                            {/* Count Badge */}
                            <span
                              className="font-lilita text-white absolute"
                              style={{
                                fontSize: '20px',
                                lineHeight: '23px',
                                left: '52px',
                                top: '49px',
                                textShadow: '1px 2px 0px #000000',
                              }}
                            >
                              x{count}
                            </span>
                          </motion.div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 w-full">
                      <p
                        className="font-lilita text-white/80 text-center"
                        style={{
                          fontSize: '20px',
                          lineHeight: '23px',
                          textShadow: '1px 1px 0px #000',
                        }}
                      >
                        Oops! Your vault is empty
                      </p>
                      <p
                        className="font-lilita text-white/80 text-center"
                        style={{
                          fontSize: '20px',
                          lineHeight: '23px',
                          textShadow: '1px 1px 0px #000',
                        }}
                      >
                        Go grab some chests, plenty!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Open Chest Modal */}
      <OpenChestModal
        isOpen={isOpenChestModalOpen}
        chest={selectedChest}
        onClose={() => {
          setIsOpenChestModalOpen(false);
          setSelectedChest(null);
        }}
        onConfirm={handleOpenChest}
        isLoading={openChestMutation.isPending}
      />

      {/* Reward Modal */}
      <ChestRewardModal
        isOpen={isRewardModalOpen}
        rewards={rewardAmount}
        onClose={handleClaimReward}
      />
    </>
  );
};
